import {
    BaseFeatureArgs,
    FixedFilterGenerator,
    InitialQueryState,
    QueryManagerOptions,
    SkipOrArgs,
} from '@common/types/queryState';
import { composeGenerators } from '@common/utils/query/composeGenerators';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { COLLECTION_KEYS } from '../mappers';
import { useCollections, UseCollectionsArgs } from './useCollections';

export interface UseCollectionByIdArgs extends BaseFeatureArgs {
    collectionId: string;
}

const collectionByIdFilterGenerator: FixedFilterGenerator<
    UseCollectionByIdArgs
> = (args) => {
    return [`id:${args.collectionId}`];
};

export const useCollectionById = (
    initialArgs: InitialQueryState = {},
    args: SkipOrArgs<UseCollectionByIdArgs>,
    options?: QueryManagerOptions,
) => {
    const queryArgs: SkipOrArgs<UseCollectionsArgs> = useMemo(() => {
        if (args === skipToken || !args.collectionId) return skipToken;

        const mergedGenerators = composeGenerators(
            collectionByIdFilterGenerator,
            args.fixedFilterGenerators,
        );

        return {
            ...args,
            fixedFilterGenerators: mergedGenerators,
            keyMap: COLLECTION_KEYS,
        } as UseCollectionsArgs;
    }, [args]);

    const { data, ...queryResult } = useCollections(
        { sorting: [{ id: 'created', desc: true }], ...initialArgs },
        queryArgs,
        {
            ...options,
            paramsOverride: {
                ...options?.paramsOverride,
                pageIndex: 0,
                pageSize: 1,
            },
        },
    );

    return {
        data: data?.[0],
        ...queryResult,
    };
};
